import { useCallback, useMemo, useState } from 'react';
import { useAllActiveCarts } from '@/hooks/queries/cart/useCartQueries';
import { useClearCart, useBulkClearCarts } from '@/hooks/mutations/cart/useCartMutations';
import type { WorkflowCart } from '@/services/cart';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import dayjs from 'dayjs';
import { IssueWorkflowModal } from '@/components/iem/workflows/issue';
import { TransfersShippingModal } from '@/components/transfers-shipping/TransfersShippingModal';
import { SendForMaintenanceModal } from '@/components/maintenance';
import type { TransfersShippingType } from '@/services/transfers-shipping';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';

const isTransferCart = (wf: string) => wf === 'transfer' || wf === 'shipment';
const isMaintenanceCart = (wf: string) => wf === 'maintenance-send' || wf === 'maintenance-receive';

interface SelectedCart {
  customerId: string;
  customerName: string;
}

export const ReservedStockCartsPage = () => {
  const { data: carts, isLoading, error } = useAllActiveCarts();
  const [selectedCart, setSelectedCart] = useState<SelectedCart | null>(null);
  const [cancelCart, setCancelCart] = useState<WorkflowCart | null>(null);
  const [selectedTransferType, setSelectedTransferType] = useState<TransfersShippingType | null>(null);
  const [maintenanceSendOpen, setMaintenanceSendOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCartIds, setSelectedCartIds] = useState<Set<string>>(new Set());
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const clearCartMutation = useClearCart();
  const bulkClearMutation = useBulkClearCarts();

  // Only show carts with items - empty carts are kept for reuse but hidden
  const visibleCarts = useMemo(
    () => carts?.filter((c) => (c.item_count ?? 0) > 0) ?? [],
    [carts],
  );

  const issueCarts = useMemo(
    () => visibleCarts.filter((c) => !isTransferCart(c.workflow_type) && !isMaintenanceCart(c.workflow_type)),
    [visibleCarts],
  );
  const transferCarts = useMemo(
    () => visibleCarts.filter((c) => isTransferCart(c.workflow_type)),
    [visibleCarts],
  );
  const maintenanceCarts = useMemo(
    () => visibleCarts.filter((c) => isMaintenanceCart(c.workflow_type)),
    [visibleCarts],
  );

  const displayedCarts = activeTab === 0 ? issueCarts : activeTab === 1 ? transferCarts : maintenanceCarts;
  const { sortField, sortDirection, onSort, sortedRows: sortedCarts } = useTableSort(
    displayedCarts as unknown as Record<string, unknown>[],
  );

  const isBulkPending = bulkClearMutation.isPending;
  const isAnyPending = clearCartMutation.isPending || isBulkPending;

  const handleTabChange = useCallback((_e: React.SyntheticEvent, v: number) => {
    setActiveTab(v);
    setSelectedCartIds(new Set());
  }, []);

  const handleToggleSelect = useCallback((cartId: string) => {
    setSelectedCartIds((prev) => {
      const next = new Set(prev);
      if (next.has(cartId)) {
        next.delete(cartId);
      } else {
        next.add(cartId);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedCartIds((prev) => {
      if (prev.size === displayedCarts.length) {
        return new Set();
      }
      return new Set(displayedCarts.map((c) => c.id));
    });
  }, [displayedCarts]);

  const handleCancelDialogClose = useCallback(() => {
    if (!clearCartMutation.isPending) {
      setCancelCart(null);
    }
  }, [clearCartMutation.isPending]);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelCart?.customer_id) return;
    try {
      const result = await clearCartMutation.mutateAsync({
        customerId: cancelCart.customer_id,
        cartId: cancelCart.id,
        workflowType: cancelCart.workflow_type,
      });
      if (result.success) {
        setCancelCart(null);
      }
    } catch {
      // Errors are handled by the mutation notifications.
    }
  }, [cancelCart, clearCartMutation]);

  const handleBulkCancel = useCallback(async () => {
    const cartsToCancel = displayedCarts.filter((c) => selectedCartIds.has(c.id));
    if (cartsToCancel.length === 0) return;
    try {
      await bulkClearMutation.mutateAsync(
        cartsToCancel.map((c) => ({
          customerId: c.customer_id,
          cartId: c.id,
          workflowType: c.workflow_type,
        })),
      );
    } finally {
      setBulkCancelOpen(false);
      setSelectedCartIds(new Set());
    }
  }, [displayedCarts, selectedCartIds, bulkClearMutation]);

  const bulkCancelCount = selectedCartIds.size;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">Failed to load reserved stock carts.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Saved Carts
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        View carts with items staged for issue or transfer. Reserved items have stock locked;
        pending items await size selection.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="Saved carts tabs">
          <Tab label={`Saved Issue Carts (${issueCarts.length})`} />
          <Tab label={`Saved Transfer Carts (${transferCarts.length})`} />
          <Tab label={`Saved Maintenance Carts (${maintenanceCarts.length})`} />
        </Tabs>
      </Box>

      {displayedCarts.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            color="error"
            variant="outlined"
            disabled={selectedCartIds.size === 0 || isAnyPending}
            onClick={() => setBulkCancelOpen(true)}
          >
            Cancel Selected ({selectedCartIds.size})
          </Button>
        </Stack>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedCartIds.size > 0 && selectedCartIds.size < displayedCarts.length}
                      checked={displayedCarts.length > 0 && selectedCartIds.size === displayedCarts.length}
                      onChange={handleToggleSelectAll}
                      disabled={displayedCarts.length === 0 || isAnyPending}
                      inputProps={{ 'aria-label': 'Select all carts' }}
                    />
                  </TableCell>
                  {activeTab === 0 ? (
                    <>
                      <SortableTableCell field="customer_display_name" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Customer</SortableTableCell>
                      <SortableTableCell field="item_count" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Items</SortableTableCell>
                    </>
                  ) : (
                    <>
                      <SortableTableCell field="workflow_type" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Type</SortableTableCell>
                      <SortableTableCell field="item_count" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Items</SortableTableCell>
                    </>
                  )}
                  <SortableTableCell field="created_at" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Date Created</SortableTableCell>
                  <SortableTableCell field="updated_at" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Date Last Updated</SortableTableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedCarts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {activeTab === 0
                        ? 'No saved issue carts found.'
                        : activeTab === 1
                          ? 'No saved transfer carts found.'
                          : 'No saved maintenance carts found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  (sortedCarts as unknown as WorkflowCart[]).map((cart) => (
                    <TableRow
                      key={cart.id}
                      hover
                      selected={selectedCartIds.has(cart.id)}
                      onClick={() => {
                        if (activeTab === 0 && cart.customer_id) {
                          setSelectedCart({
                            customerId: cart.customer_id,
                            customerName: cart.customer_display_name || 'Unknown Customer',
                          });
                        } else if (activeTab === 1) {
                          setSelectedTransferType(cart.workflow_type as TransfersShippingType);
                        } else if (activeTab === 2 && cart.workflow_type === 'maintenance-send') {
                          setMaintenanceSendOpen(true);
                        }
                      }}
                      sx={{
                        cursor: (activeTab === 0 && cart.customer_id) || activeTab === 1 || (activeTab === 2 && cart.workflow_type === 'maintenance-send') ? 'pointer' : 'default',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCartIds.has(cart.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleSelect(cart.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isAnyPending}
                          inputProps={{ 'aria-label': `Select cart ${cart.customer_display_name || cart.id}` }}
                        />
                      </TableCell>
                      {activeTab === 0 ? (
                        <>
                          <TableCell>{cart.customer_display_name || 'Unknown Customer'}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {`${cart.reserved_count ?? 0} reserved, ${cart.unsized_count ?? 0} pending sizing`}
                            </Typography>
                          </TableCell>
                        </>
                      ) : activeTab === 1 ? (
                        <>
                          <TableCell>
                            {cart.workflow_type === 'shipment' ? 'Shipment' : 'Transfer'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {cart.item_count ?? 0} items
                            </Typography>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            {cart.workflow_type === 'maintenance-send' ? 'Send' : 'Receive'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {cart.item_count ?? 0} items
                            </Typography>
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        <Typography variant="body2">
                          {cart.created_at
                            ? dayjs(cart.created_at).format('DD MMM YYYY HH:mm')
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {cart.updated_at
                            ? dayjs(cart.updated_at).format('DD MMM YYYY HH:mm')
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={!cart.customer_id || isAnyPending}
                          onClick={(event) => {
                            event.stopPropagation();
                            setCancelCart(cart);
                          }}
                        >
                          Cancel
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={cancelCart !== null}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-cart-dialog-title"
        aria-describedby="cancel-cart-dialog-description"
      >
        <DialogTitle id="cancel-cart-dialog-title">Cancel Reserved Cart</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-cart-dialog-description">
            This will release all reserved items for{' '}
            {cancelCart?.customer_display_name || 'this customer'}. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} disabled={clearCartMutation.isPending}>
            Keep Cart
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={clearCartMutation.isPending}
          >
            Cancel Cart
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkCancelOpen}
        onClose={() => !isBulkPending && setBulkCancelOpen(false)}
        aria-labelledby="bulk-cancel-dialog-title"
        aria-describedby="bulk-cancel-dialog-description"
      >
        <DialogTitle id="bulk-cancel-dialog-title">Cancel Selected Carts</DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-cancel-dialog-description">
            This will release all reserved items for {bulkCancelCount} cart(s). This action cannot be
            undone. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkCancelOpen(false)} disabled={isBulkPending}>
            Keep Carts
          </Button>
          <Button
            onClick={handleBulkCancel}
            color="error"
            variant="contained"
            disabled={isBulkPending}
          >
            {isBulkPending ? 'Canceling...' : `Cancel ${bulkCancelCount} Cart(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      <IssueWorkflowModal
        open={selectedCart !== null}
        onClose={() => setSelectedCart(null)}
        fromSavedCart
        memberName={selectedCart?.customerName}
        customerId={selectedCart?.customerId}
      />

      {selectedTransferType && (
        <TransfersShippingModal
          open
          onClose={() => setSelectedTransferType(null)}
          transferType={selectedTransferType}
        />
      )}

      <SendForMaintenanceModal
        open={maintenanceSendOpen}
        onClose={() => setMaintenanceSendOpen(false)}
      />
    </Box>
  );
};
